import AuthenticationModeField from '@UI/Pages/Administration/Sso/SsoFormContainer/Fields/AuthenticationModeField';
import CallbackUrlField from '@UI/Pages/Administration/Sso/SsoFormContainer/Fields/CallbackUrlField';
import ClientSecretField from '@UI/Pages/Administration/Sso/SsoFormContainer/Fields/ClientSecretField';
import EmailDomainsField from '@UI/Pages/Administration/Sso/SsoFormContainer/Fields/EmailDomainsField';
import SectionLabel from '@UI/Pages/Administration/Sso/SsoFormContainer/Shared/SectionLabel';
import type IdentityProviderFormData from '@UI/Pages/Administration/Sso/SsoFormContainer/FormData/IdentityProviderFormData';
import type LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';
import type IdentityProviderView from '@UI/Store/Sso/Views/IdentityProviderView';
import { css } from '@emotion/react';
import type * as React from 'react';
import { Form, InputText, Switch } from '@verclam/ui-kit';

const formStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.75rem;
`;

const groupStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

const twoColumnRowStyle = css`
	display: flex;
	gap: 1rem;
	flex-wrap: wrap;
`;

const twoColumnFieldStyle = css`
	flex: 1 1 190px;
	min-width: 190px;
`;

type Props = {
	readonly formId: string;
	readonly onSubmit: () => void;
	readonly formData: IdentityProviderFormData;
	readonly identityProvider: IdentityProviderView | null;
	readonly ssoModes: readonly LabeledValueView[] | null;
	readonly onModeChange: (value: string) => void;
	readonly onIssuerUrlChange: (value: string) => void;
	readonly onClientIdChange: (value: string) => void;
	readonly onClientSecretChange: (value: string) => void;
	readonly onEmailDomainsChange: (domains: readonly string[]) => void;
	readonly onIsEnabledChange: (value: boolean) => void;
};

/**
 * Assembles the editable configuration: mode, OpenID Connect connection (redirect URI, issuer, client
 * identifier, secret), domains, activation. Knows nothing of loading, saving, deleting or testing.
 */
const IdentityProviderForm: React.FC<Props> = ({
	formId,
	onSubmit,
	formData,
	identityProvider,
	ssoModes,
	onModeChange,
	onIssuerUrlChange,
	onClientIdChange,
	onClientSecretChange,
	onEmailDomainsChange,
	onIsEnabledChange,
}) => {
	return (
		<Form id={formId} onSubmit={onSubmit}>
			<div css={formStyle}>
				<AuthenticationModeField
					ssoModes={ssoModes}
					mode={formData.mode}
					isEnabled={formData.isEnabled}
					onChange={onModeChange}
					errorMessage={formData.getValidationMessage('mode')}
					isFormFieldValid={formData.isValid('mode')}
				/>

				<div css={groupStyle}>
					<SectionLabel>CONNEXION OPENID CONNECT</SectionLabel>
					<CallbackUrlField identityProvider={identityProvider} />
					<div css={twoColumnRowStyle}>
						<div css={twoColumnFieldStyle}>
							<InputText
								name="issuerUrl"
								label="URL du fournisseur (issuer)"
								placeholder="https://login.microsoftonline.com/{tenantId}/v2.0"
								required
								value={formData.issuerUrl}
								onChange={onIssuerUrlChange}
								errorMessage={formData.getValidationMessage('issuerUrl')}
								isFormFieldValid={formData.isValid('issuerUrl')}
							/>
						</div>
						<div css={twoColumnFieldStyle}>
							<InputText
								name="clientId"
								label="Identifiant client"
								required
								value={formData.clientId}
								onChange={onClientIdChange}
								errorMessage={formData.getValidationMessage('clientId')}
								isFormFieldValid={formData.isValid('clientId')}
							/>
						</div>
					</div>
					<ClientSecretField
						key={
							identityProvider === null
								? 'creation'
								: identityProvider.clientSecretRegisteredAt.toISOString()
						}
						identityProvider={identityProvider}
						value={formData.clientSecret}
						onChange={onClientSecretChange}
						errorMessage={formData.getValidationMessage('clientSecret')}
						isFormFieldValid={formData.isValid('clientSecret')}
					/>
				</div>

				<EmailDomainsField
					domains={formData.emailDomains}
					onChange={onEmailDomainsChange}
					errorMessage={formData.getValidationMessage('emailDomains')}
					isFormFieldValid={formData.isValid('emailDomains')}
				/>

				<div css={groupStyle}>
					<SectionLabel>ACTIVATION</SectionLabel>
					<Switch
						name="isEnabled"
						label="Activer la connexion par le fournisseur d’identité"
						checked={formData.isEnabled}
						onChange={onIsEnabledChange}
					/>
				</div>
			</div>
		</Form>
	);
};

export default IdentityProviderForm;
